"use client";

import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { ArrowRight, GraduationCap, Cpu, Code, Trophy, Sparkles } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
export default function TeachingTeam() {
  const [lang, setLang] = useState<"vi" | "en">("vi");
  useEffect(() => {
    const getInitialLang = (): "vi" | "en" => {
      if (typeof window === "undefined") return "vi";
      const saved = localStorage.getItem("roboto_lang");
      return saved === "en" ? "en" : "vi";
    };
    setLang(getInitialLang());
    const handleLangChange = (e: Event) => {
      const customEvent = e as CustomEvent<{
        lang?: "vi" | "en";
      }>;
      if (customEvent.detail && (customEvent.detail.lang === "vi" || customEvent.detail.lang === "en")) {
        setLang(customEvent.detail.lang);
      } else {
        const saved = localStorage.getItem("roboto_lang");
        setLang(saved === "en" ? "en" : "vi");
      }
    };
    window.addEventListener("roboto-lang-change", handleLangChange);
    window.addEventListener("storage", handleLangChange);
    return () => {
      window.removeEventListener("roboto-lang-change", handleLangChange);
      window.removeEventListener("storage", handleLangChange);
    };
  }, []);
  const content = {
    vi: {
      heading: "ĐỘI NGŨ ĐÀO TẠO",
      paragraph: "Tại ROBOTO, đội ngũ giảng viên giàu kinh nghiệm và tâm huyết luôn đồng hành, truyền cảm hứng và dẫn dắt học sinh qua từng dự án lắp ráp robot, lập trình và sáng tạo kỹ thuật thực tế.",
      cta: "Xem đội ngũ giảng viên"
    },
    en: {
      heading: "OUR TEACHING TEAM",
      paragraph: "At ROBOTO, our experienced and dedicated instructors guide students through hands-on engineering, LEGO robotics, and coding projects, fostering creativity and problem-solving skills at every step.",
      cta: "Meet our faculty"
    }
  };
  const tracksData = {
    vi: [{
      id: "stem-lego",
      title: "Khối LEGO & Early STEM",
      subtitle: "Đội ngũ giảng viên Mầm Nông & Tiểu Học",
      badge: "AGES 4–7",
      icon: Sparkles,
      image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/teaching_team_lego_stem.png",
      srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/teaching_team_lego_stem.png-wvc-srcset",
      sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/teaching_team_lego_stem.png-wvc-sizes",
      alt: "Đội ngũ giảng viên hướng dẫn học sinh mầm nông lắp ráp LEGO Robotics trong phòng lab"
    }, {
      id: "hardware-eng",
      title: "Khối Kỹ Thuật & Phần Cứng",
      subtitle: "Chuyên gia chế tạo cơ khí & cảm biến",
      badge: "AGES 8–12",
      icon: Cpu,
      image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/teaching_team_hardware.png",
      srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/teaching_team_hardware.png-wvc-srcset",
      sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/teaching_team_hardware.png-wvc-sizes",
      alt: "Giảng viên kỹ thuật hỗ trợ học sinh thực hành thiết kế mạch điện tử và khung xe robot"
    }, {
      id: "coding-ai",
      title: "Khối Lập Trình & AI",
      subtitle: "Kỹ sư lập trình & tư duy thuật toán",
      badge: "AGES 10–16",
      icon: Code,
      image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/teaching_team_coding.png",
      srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/teaching_team_coding.png-wvc-srcset",
      sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/teaching_team_coding.png-wvc-sizes",
      alt: "Lớp học lập trình Python và điều khiển robot cùng giảng viên hướng dẫn trực tiếp"
    }, {
      id: "competition",
      title: "Khối Huấn Luyện Thi Đấu",
      subtitle: "HLV giải đấu WRO & VEX Robotics",
      badge: "COMPETITION TRACK",
      icon: Trophy,
      image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/teaching_team_competition.png",
      srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/teaching_team_competition.png-wvc-srcset",
      sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/teaching_team_competition.png-wvc-sizes",
      alt: "Huấn luyện viên cùng đội tuyển robot thử nghiệm chiến thuật thi đấu quốc tế"
    }],
    en: [{
      id: "stem-lego",
      title: "LEGO & Early STEM Faculty",
      subtitle: "Early childhood & STEM mentors",
      badge: "AGES 4–7",
      icon: Sparkles,
      image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/teaching_team_lego_stem.png",
      srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/teaching_team_lego_stem.png-wvc-srcset",
      sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/teaching_team_lego_stem.png-wvc-sizes",
      alt: "Instructors guiding young students in LEGO robotics building"
    }, {
      id: "hardware-eng",
      title: "Hardware & Electronics Faculty",
      subtitle: "Mechanics & sensor specialists",
      badge: "AGES 8–12",
      icon: Cpu,
      image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/teaching_team_hardware.png",
      srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/teaching_team_hardware.png-wvc-srcset",
      sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/teaching_team_hardware.png-wvc-sizes",
      alt: "Engineering mentors teaching electronics assembly and robot chassis design"
    }, {
      id: "coding-ai",
      title: "Software & AI Faculty",
      subtitle: "Algorithm & coding instructors",
      badge: "AGES 10–16",
      icon: Code,
      image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/teaching_team_coding.png",
      srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/teaching_team_coding.png-wvc-srcset",
      sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/teaching_team_coding.png-wvc-sizes",
      alt: "Software mentors assisting students with Python robotics algorithms"
    }, {
      id: "competition",
      title: "Competition Coaching Faculty",
      subtitle: "WRO & VEX certified coaches",
      badge: "COMPETITION TRACK",
      icon: Trophy,
      image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/teaching_team_competition.png",
      srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/teaching_team_competition.png-wvc-srcset",
      sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/teaching_team_competition.png-wvc-sizes",
      alt: "Coaches fine-tuning competition arena strategies with student team"
    }]
  };
  const t = content[lang];
  const tracks = tracksData[lang];
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.08
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.35,
        ease: [0.215, 0.61, 0.355, 1]
      }
    }
  };
  return <section id="teachingteam" data-nav="light" className="relative overflow-hidden bg-background text-foreground py-16 sm:py-20 lg:py-24 isolate">
      {/* Decorative background grid subtle accent */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 -z-10 opacity-40 bg-[radial-gradient(ellipse_at_center,var(--border)_1px,transparent_1px)] bg-[size:24px_24px]" />

      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 text-center">
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: "-50px"
      }} className="flex flex-col items-center">
          {/* Precision Spec Rail accent in Lime Green */}
          <motion.div variants={itemVariants} className="flex items-center justify-center gap-2 mb-6">
            <span className="h-1 w-10 rounded-lg bg-secondary" />
            <GraduationCap className="size-5 text-secondary" aria-hidden="true" />
            <span className="h-1 w-10 rounded-lg bg-secondary" />
          </motion.div>

          {/* Heading H2 */}
          <motion.h2 variants={itemVariants} className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground text-balance mb-6">
            {t.heading}
          </motion.h2>

          {/* Introductory Paragraph */}
          <motion.p variants={itemVariants} className="text-muted-foreground text-base sm:text-lg lg:text-xl leading-relaxed max-w-2xl mb-12">
            {t.paragraph}
          </motion.p>

          {/* Generic Lab & Classroom Photography Grid by Track */}
          <motion.div variants={itemVariants} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 w-full text-left mb-12">
            {tracks.map((track, i) => {
            const IconComponent = track.icon;
            return <div key={track.id} data-index={i} className="group relative flex flex-col overflow-hidden rounded-xl border border-border bg-card p-4 transition-[border-color,box-shadow] duration-200 hover:border-secondary hover:shadow-lg">
                  {/* Image container */}
                  <div className="relative aspect-4/3 w-full overflow-hidden rounded-lg bg-muted mb-4">
                    <img src={track.image} data-wvc-srcset={track.srcset} data-wvc-sizes={track.sizes} alt={track.alt} loading="lazy" className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105" />
                    {/* Dark gradient vignette for clarity */}
                    <div className="absolute inset-0 bg-linear-to-t from-black/60 via-transparent to-transparent pointer-events-none" />

                    {/* Track Badge overlay */}
                    <span className="absolute top-3 left-3 inline-flex items-center gap-1.5 rounded-md bg-background/90 backdrop-blur-xs px-2.5 py-1 text-xs font-bold text-foreground border border-border shadow-xs">
                      <IconComponent className="size-3.5 text-secondary" />
                      <span>{track.badge}</span>
                    </span>
                  </div>

                  {/* Card text */}
                  <div className="flex flex-1 flex-col justify-between">
                    <div>
                      <h3 className="font-serif text-lg font-semibold text-foreground group-hover:text-primary transition-colors">
                        {track.title}
                      </h3>
                      <p className="mt-1 text-xs text-muted-foreground leading-relaxed">
                        {track.subtitle}
                      </p>
                    </div>

                    {/* Technical spec bottom line */}
                    <div className="mt-4 pt-3 border-t border-border/60 flex items-center justify-between text-xs font-mono text-muted-foreground">
                      <span className="text-secondary font-bold">ROBOTO LAB</span>
                      <span className="opacity-70">FACULTY // {i + 1}</span>
                    </div>
                  </div>
                </div>;
          })}
          </motion.div>

          {/* CTA Button */}
          <motion.div variants={itemVariants}>
            <Button size="lg" className="group rounded-lg shadow-md" asChild>
              <Link to="/TeachersPage">
                <span>{t.cta}</span>
                <ArrowRight className="size-4 transition-transform duration-200 group-hover:translate-x-1" />
              </Link>
            </Button>
          </motion.div>
        </motion.div>
      </div>
    </section>;
}